import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getEAProject, getEADeliverables, downloadEAArtifact } from '../services/ea-api'
import type { EAArtifactListItem } from '../types/ea'
import EAVendorBadge from '../components/ea/EAVendorBadge'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const PHASE_COLORS: Record<string, string> = {
  requirements: '#3b82f6',
  design: '#8b5cf6',
  deployment: '#10b981',
  qc: '#f59e0b',
  deliverables: '#06b6d4',
}

const PHASE_LABELS: Record<string, string> = {
  requirements: 'Requirements',
  design: 'Design',
  deployment: 'Deployment',
  qc: 'QC',
  deliverables: 'Deliverables',
}

const FORMAT_ICONS: Record<string, string> = {
  md: 'fa-solid fa-file-lines',
  docx: 'fa-solid fa-file-word',
  xlsx: 'fa-solid fa-file-excel',
  png: 'fa-solid fa-file-image',
  drawio: 'fa-solid fa-diagram-project',
  mmd: 'fa-solid fa-diagram-project',
  cli: 'fa-solid fa-terminal',
  xml: 'fa-solid fa-file-code',
  tf: 'fa-solid fa-cube',
  yml: 'fa-solid fa-file-code',
  json: 'fa-solid fa-file-code',
}

interface VendorPhaseGroup {
  vendor: string
  phases: Record<string, EAArtifactListItem[]>
}

export default function EADeliverables() {
  const { projectId } = useParams<{ projectId: string }>()

  const { data: project } = useQuery({
    queryKey: ['ea-project', projectId],
    queryFn: () => getEAProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: deliverablesData, isLoading } = useQuery({
    queryKey: ['ea-deliverables', projectId],
    queryFn: () => getEADeliverables(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const deliverables: EAArtifactListItem[] = Array.isArray(deliverablesData) ? deliverablesData : []

  // Group by vendor, then by phase
  const grouped = useMemo(() => {
    const vendorMap: Record<string, Record<string, EAArtifactListItem[]>> = {}
    const noVendor: Record<string, EAArtifactListItem[]> = {}

    deliverables.forEach((d) => {
      const vendor = d.vendor || '_none'
      const target = vendor === '_none' ? noVendor : (vendorMap[vendor] = vendorMap[vendor] || {})
      if (!target[d.phase]) target[d.phase] = []
      target[d.phase].push(d)
    })

    const result: VendorPhaseGroup[] = []

    // Named vendors first
    Object.keys(vendorMap).sort().forEach((vendor) => {
      result.push({ vendor, phases: vendorMap[vendor] })
    })

    // Ungrouped last
    if (Object.keys(noVendor).length > 0) {
      result.push({ vendor: 'General', phases: noVendor })
    }

    return result
  }, [deliverables])

  const handleDownload = async (artifactId: string, filename: string) => {
    try {
      const response = await downloadEAArtifact(artifactId)
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch {
      // Download error handling - could be enhanced with toast
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading deliverables...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/ea/projects/${projectId}`}
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: 'rgba(148, 163, 184, 0.8)',
            }}
            aria-label="Back to project"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Deliverables</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {project?.project_name || 'Loading...'}
            </p>
          </div>
        </div>
        <button
          disabled
          className="px-5 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 cursor-not-allowed opacity-40"
          style={{
            background: 'rgba(59, 130, 246, 0.15)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            color: '#60a5fa',
          }}
          title="Download All — coming soon"
        >
          <i className="fa-solid fa-download"></i>
          <span>Download All</span>
        </button>
      </div>

      {/* Summary card */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <div className="flex items-center space-x-6">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'rgba(6, 182, 212, 0.1)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
            }}
          >
            <i className="fa-solid fa-folder-open text-2xl" style={{ color: '#06b6d4' }}></i>
          </div>
          <div>
            <div className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{deliverables.length}</div>
            <div className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Promoted deliverable{deliverables.length !== 1 ? 's' : ''}
            </div>
          </div>
          <div className="flex items-center space-x-4 ml-auto">
            {grouped.map((g) => (
              <div key={g.vendor} className="text-center">
                <div className="text-lg font-bold" style={{ color: '#e2e8f0' }}>
                  {Object.values(g.phases).reduce((sum, arr) => sum + arr.length, 0)}
                </div>
                <div className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{g.vendor}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Deliverables grouped by vendor, then phase */}
      {deliverables.length > 0 ? (
        <div className="space-y-8">
          {grouped.map((group) => (
            <div key={group.vendor}>
              <h2 className="text-sm font-semibold uppercase tracking-wider mb-4 flex items-center space-x-2">
                {group.vendor !== 'General' ? (
                  <>
                    <EAVendorBadge vendor={group.vendor} size="md" />
                    <span style={{ color: 'rgba(148, 163, 184, 0.4)' }}>
                      ({Object.values(group.phases).reduce((sum, arr) => sum + arr.length, 0)} file{Object.values(group.phases).reduce((sum, arr) => sum + arr.length, 0) !== 1 ? 's' : ''})
                    </span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-globe" style={{ color: 'rgba(148, 163, 184, 0.6)' }}></i>
                    <span style={{ color: 'rgba(148, 163, 184, 0.6)' }}>General</span>
                  </>
                )}
              </h2>

              {Object.entries(group.phases)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([phase, artifacts]) => {
                  const phaseColor = PHASE_COLORS[phase] || '#94a3b8'
                  return (
                    <div key={phase} className="mb-6">
                      <div className="flex items-center space-x-2 mb-3 ml-1">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: phaseColor }}
                        />
                        <span className="text-xs font-medium uppercase tracking-wider" style={{ color: phaseColor }}>
                          {PHASE_LABELS[phase] || phase}
                        </span>
                        <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.4)' }}>
                          ({artifacts.length})
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {artifacts.map((artifact) => {
                          const formatIcon = FORMAT_ICONS[artifact.file_format] || 'fa-solid fa-file'
                          return (
                            <div
                              key={artifact.id}
                              className="rounded-xl p-4 transition-all duration-200 hover:scale-[1.01]"
                              style={{
                                background: 'rgba(30, 41, 59, 0.5)',
                                border: '1px solid rgba(100, 180, 255, 0.1)',
                              }}
                            >
                              <div className="flex items-start space-x-3">
                                <div
                                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{
                                    background: `${phaseColor}15`,
                                    border: `1px solid ${phaseColor}33`,
                                  }}
                                >
                                  <i className={`${formatIcon} text-lg`} style={{ color: phaseColor }}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4
                                    className="text-sm font-semibold truncate mb-1"
                                    style={{ color: '#e2e8f0' }}
                                    title={artifact.filename}
                                  >
                                    {artifact.filename}
                                  </h4>
                                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                    <span
                                      className="px-1.5 py-0.5 text-[10px] font-medium rounded uppercase"
                                      style={{
                                        background: `${phaseColor}22`,
                                        color: phaseColor,
                                        border: `1px solid ${phaseColor}44`,
                                      }}
                                    >
                                      {artifact.file_format}
                                    </span>
                                    <span
                                      className="px-1.5 py-0.5 text-[10px] font-medium rounded"
                                      style={{
                                        background: 'rgba(100, 116, 139, 0.15)',
                                        color: 'rgba(148, 163, 184, 0.7)',
                                      }}
                                    >
                                      v{artifact.version}
                                    </span>
                                    {artifact.file_size_bytes != null && (
                                      <span className="text-[10px]" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                                        {artifact.file_size_bytes < 1024
                                          ? `${artifact.file_size_bytes} B`
                                          : artifact.file_size_bytes < 1048576
                                          ? `${(artifact.file_size_bytes / 1024).toFixed(1)} KB`
                                          : `${(artifact.file_size_bytes / 1048576).toFixed(1)} MB`}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px]" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                                    Promoted {new Date(artifact.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDownload(artifact.id, artifact.filename)}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 hover:scale-110"
                                  style={{
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    border: '1px solid rgba(59, 130, 246, 0.2)',
                                    color: '#60a5fa',
                                  }}
                                  aria-label={`Download ${artifact.filename}`}
                                  title="Download"
                                >
                                  <i className="fa-solid fa-download text-sm"></i>
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
            </div>
          ))}
        </div>
      ) : (
        /* Empty state */
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: 'rgba(6, 182, 212, 0.1)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
            }}
          >
            <i
              className="fa-solid fa-box-open text-3xl"
              style={{ color: 'rgba(6, 182, 212, 0.5)' }}
            ></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            No deliverables yet
          </h3>
          <p className="mb-4 max-w-md mx-auto" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Deliverables will appear here once artifacts pass QC review and are promoted. Complete the QC review process to generate deliverables.
          </p>
          <Link
            to={`/ea/projects/${projectId}/qc`}
            className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
              border: '1px solid rgba(96, 165, 250, 0.4)',
              color: '#e2e8f0',
            }}
          >
            <i className="fa-solid fa-clipboard-check"></i>
            <span>Go to QC Review</span>
          </Link>
        </div>
      )}
    </div>
  )
}
